interface GateInfo {
    id: number;
    type: number;
    information: string;
}

const info: { [key: string]: GateInfo } = {
    h: {
        id: 0,
        type: 0,
        information: String.raw`Single-qubit Hadamard gate.
        This gate is a \(\pi\) rotation about the X+Z axis, and has the effect of changing
        computation basis from \(|0\rangle,|1\rangle\) to \(|+\rangle,|-\rangle\) and vice-versa. <br /><br />
        <strong>Matrix Representation:</strong>
        \begin{split}H = \frac{1}{\sqrt{2}}
        \begin{pmatrix}
            1 & 1 \\
            1 & -1
        \end{pmatrix}\end{split}`
    },
    x: {
        id: 1,
        type: 0,
        information: String.raw`The single-qubit Pauli-X gate (\(\sigma_x\)). Equivalent to a 
        \(\pi\) radian rotation about the X axis. <br /><br />
        <strong>Matrix Representation:</strong>
        \begin{split}X = \begin{pmatrix}
        0 & 1 \\
        1 & 0
        \end{pmatrix}\end{split}`
    },
    y: {
        id: 2,
        type: 0,
        information: String.raw`The single-qubit Pauli-Y gate (\(\sigma_y\)). Equivalent to a 
        \(\pi\) radian rotation about the Y axis. <br /><br />
        <strong>Matrix Representation:</strong>
        \begin{split}X = \begin{pmatrix}
        0 & -i \\
        i & 0
        \end{pmatrix}\end{split}`
    },
    z: {
        id: 3,
        type: 0,
        information: String.raw`The single-qubit Pauli-Z gate (\(\sigma_z\)). Equivalent to a 
        \(\pi\) radian rotation about the Z axis. <br /><br />
        <strong>Matrix Representation:</strong>
        \begin{split}Z = \begin{pmatrix}
        1 & 0 \\
        0 & -1
        \end{pmatrix}\end{split}`
    },
    rx: {
        id: 4,
        type: 0,
        information: String.raw`Single-qubit rotation about the X axis. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{align}
            \begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\
                \begin{split}RX(\theta) & = \exp\left(-i \th X\right) \\ & =
                    \begin{pmatrix}
                        \cos\left(\th\right)   & -i\sin\left(\th\right) \\
                        -i\sin\left(\th\right) & \cos\left(\th\right)
                    \end{pmatrix}  
                \end{split}
            \end{aligned}
        \end{align}`
    },
    ry: {
        id: 5,
        type: 0,
        information: String.raw`Single-qubit rotation about the Y axis. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{align}
            \begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\
                \begin{split}RY(\theta) & = \exp\left(-i \th Y\right) \\ & =
                    \begin{pmatrix}
                        \cos\left(\th\right) & -\sin\left(\th\right) \\
                        \sin\left(\th\right) & \cos\left(\th\right)
                    \end{pmatrix}
                \end{split}
            \end{aligned}
        \end{align}`
    },
    rz: {
        id: 6,
        type: 0,
        information: String.raw`Single-qubit rotation about the Z axis. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{align}
            \begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\
                \begin{split}RZ(\theta) = \exp\left(-i \th Z\right) =
                    \begin{pmatrix}
                        e^{-i\frac{\theta}{2}} & 0 \\
                        0 & e^{i\frac{\theta}{2}}
                    \end{pmatrix}
                \end{split}
            \end{aligned}
        \end{align}`
    },
    sqrtx: {
        id: 7,
        type: 0,
        information: String.raw`The single-qubit Sqrt(X) gate (\(\sqrt{X}\)). <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}\sqrt{X} = \frac{1}{2} \begin{pmatrix}
        1 + i & 1 - i \\
        1 - i & 1 + i
        \end{pmatrix}\end{split}`
    },
    sqrty: {
        id: 8,
        type: 0,
        information: String.raw`The single-qubit Sqrt(Y) gate (\(\sqrt{Y}\)). <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}\sqrt{Y} = \frac{1}{2} \begin{pmatrix}
        1 + i & -1 - i \\
        1 + i & 1 + i
        \end{pmatrix}\end{split}`
    },
    sqrtz: {
        id: 9,
        type: 0,
        information: String.raw`The single-qubit Sqrt(Z) gate (\(\sqrt{Z}\)). <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}\sqrt{Z} = \begin{pmatrix}
        1 & 0 \\
        0 & i
        \end{pmatrix}\end{split}`
    },
    cx: {
        id: 0,
        type: 1,
        information: String.raw`Controlled-X gate, also known as CNOT gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}CX\ q_0, q_1 & =
        I \otimes |0\rangle\langle0| + X \otimes |1\rangle\langle1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 0 & 0 & 1 \\
            0 & 0 & 1 & 0 \\
            0 & 1 & 0 & 0
        \end{pmatrix}\end{split} <br/>
        <strong>Note:</strong> <br/>
        In the circuit, upper qubit (\(q_0\)) is less significant, but in many textbooks,
        controlled gates are presented with the assumption of more significant qubit as control,
        which in our case would be (\(q_1\)). Thus a textbook matrix for this gate will be:
        \begin{split}CX\ q_1, q_0 & =
        |0 \rangle\langle 0| \otimes I + |1 \rangle\langle 1| \otimes X \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & 0 & 1 \\
            0 & 0 & 1 & 0
        \end{pmatrix}\end{split}`
    },
    cy: {
        id: 1,
        type: 1,
        information: String.raw`Controlled-Y gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}CY\ q_0, q_1 & =
        I \otimes |0\rangle\langle0| + Y \otimes |1\rangle\langle1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 0 & 0 & -i \\
            0 & 0 & 1 & 0 \\
            0 & i & 0 & 0
        \end{pmatrix}\end{split} <br/>
        <strong>Note:</strong> <br/>
        In the circuit, upper qubit (\(q_0\)) is less significant, but in many textbooks,
        controlled gates are presented with the assumption of more significant qubit as control,
        which in our case would be (\(q_1\)). Thus a textbook matrix for this gate will be:
        \begin{split}CY\ q_1, q_0 & =
        |0 \rangle\langle 0| \otimes I + |1 \rangle\langle 1| \otimes Y \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & 0 & -i \\
            0 & 0 & i & 0
        \end{pmatrix}\end{split}`
    },
    cz: {
        id: 2,
        type: 1,
        information: String.raw`Controlled-Z gate. In the computational basis, this gate flips the phase of the target qubit if the control qubit is in the \(|1\rangle\)
        state.<br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}CZ\ q_0, q_1 & =
        I \otimes |0\rangle\langle0| + Z \otimes |1\rangle\langle1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & 1 & 0 \\
            0 & 0 & 0 & -1
        \end{pmatrix}\end{split}`
    },
    crx: {
        id: 3,
        type: 1,
        information: String.raw`Controlled-RX gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{align}\begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\\begin{split}CRX(\theta)\ q_0, q_1 & =
        I \otimes |0\rangle\langle 0| + RX(\theta) \otimes |1\rangle\langle 1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & \cos\left(\th\right) & 0 & -i\sin\left(\th\right) \\
            0 & 0 & 1 & 0 \\
            0 & -i\sin\left(\th\right) & 0 & \cos\left(\th\right)
        \end{pmatrix}\end{split}\end{aligned}\end{align} <br/>
        <strong>Note:</strong> <br/>
        In the circuit, upper qubit (\(q_0\)) is less significant, but in many textbooks,
        controlled gates are presented with the assumption of more significant qubit as control,
        which in our case would be (\(q_1\)). Thus a textbook matrix for this gate will be:
        \begin{align}\begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\\begin{split}CRX(\theta)\ q_1, q_0 & =
        |0\rangle\langle0| \otimes I + |1\rangle\langle1| \otimes RX(\theta) \\ & =
            \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & \cos\left(\th\right)   & -i\sin\left(\th\right) \\
            0 & 0 & -i\sin\left(\th\right) & \cos\left(\th\right)
        \end{pmatrix}\end{split}\end{aligned}\end{align}`
    },
    cry: {
        id: 4,
        type: 1,
        information: String.raw`Controlled-RY gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{align}\begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\\begin{split}CRY(\theta)\ q_0, q_1 & =
        I \otimes |0\rangle\langle 0| + RY(\theta) \otimes |1\rangle\langle 1| \\ & =
        \begin{pmatrix}
            1 & 0         & 0 & 0 \\
            0 & \cos\left(\th\right) & 0 & -\sin\left(\th\right) \\
            0 & 0         & 1 & 0 \\
            0 & \sin\left(\th\right) & 0 & \cos\left(\th\right)
        \end{pmatrix}\end{split}\end{aligned}\end{align} <br/>
        <strong>Note:</strong> <br/>
        In the circuit, upper qubit (\(q_0\)) is less significant, but in many textbooks,
        controlled gates are presented with the assumption of more significant qubit as control,
        which in our case would be (\(q_1\)). Thus a textbook matrix for this gate will be:
        \begin{align}\begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\\begin{split}CRY(\theta)\ q_1, q_0 & =
        |0\rangle\langle0| \otimes I + |1\rangle\langle1| \otimes RY(\theta) \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & \cos\left(\th\right) & -\sin\left(\th\right) \\
            0 & 0 & \sin\left(\th\right) & \cos\left(\th\right)
        \end{pmatrix}\end{split}\end{aligned}\end{align}`
    },
    crz: {
        id: 5,
        type: 1,
        information: String.raw`Controlled-RZ gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{align}\begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\\begin{split}CRZ(\theta)\ q_0, q_1 & =
        I \otimes |0\rangle\langle 0| + RZ(\theta) \otimes |1\rangle\langle 1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & e^{-i\frac{\theta}{2}} & 0 & 0 \\
            0 & 0 & 1 & 0 \\
            0 & 0 & 0 & e^{i\frac{\theta}{2}}
        \end{pmatrix}\end{split}\end{aligned}\end{align} <br/>
        <strong>Note:</strong> <br/>
        In the circuit, upper qubit (\(q_0\)) is less significant, but in many textbooks,
        controlled gates are presented with the assumption of more significant qubit as control,
        which in our case would be (\(q_1\)). Thus a textbook matrix for this gate will be:
        \begin{align}\begin{aligned}\newcommand{\th}{\frac{\theta}{2}}\\\begin{split}CRZ(\theta)\ q_1, q_0 & =
        |0\rangle\langle0| \otimes I + |1\rangle\langle1| \otimes RZ(\theta) \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & e^{-i\frac{\theta}{2}} & 0 \\
            0 & 0 & 0 & e^{i\frac{\theta}{2}}
        \end{pmatrix}\end{split}\end{aligned}\end{align}`
    },
    ccx: {
        id: 6,
        type: 1,
        information: String.raw`CCX gate, also known as Toffoli gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}CCX q_0, q_1, q_2 & =
        I \otimes I \otimes |0 \rangle \langle 0| + CX \otimes |1 \rangle \langle 1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 & 0 & 0 & 0 & 0\\
            0 & 1 & 0 & 0 & 0 & 0 & 0 & 0\\
            0 & 0 & 1 & 0 & 0 & 0 & 0 & 0\\
            0 & 0 & 0 & 0 & 0 & 0 & 0 & 1\\
            0 & 0 & 0 & 0 & 1 & 0 & 0 & 0\\
            0 & 0 & 0 & 0 & 0 & 1 & 0 & 0\\
            0 & 0 & 0 & 0 & 0 & 0 & 1 & 0\\
            0 & 0 & 0 & 1 & 0 & 0 & 0 & 0
        \end{pmatrix}\end{split} <br/>
        <strong>Note:</strong> <br/>
        In the circuit, upper qubits (\(q_0, q_1\)) are less significant, but in many textbooks,
        controlled gates are presented with the assumption of more significant qubits as control,
        which in our case would be (\(q_2, q_1\)). Thus a textbook matrix for this gate will be:
        \begin{split}CCX\ q_2, q_1, q_0 & =
        |0 \rangle \langle 0| \otimes I \otimes I + |1 \rangle \langle 1| \otimes CX \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 & 0 & 0 & 0 & 0\\
            0 & 1 & 0 & 0 & 0 & 0 & 0 & 0\\
            0 & 0 & 1 & 0 & 0 & 0 & 0 & 0\\
            0 & 0 & 0 & 1 & 0 & 0 & 0 & 0\\
            0 & 0 & 0 & 0 & 1 & 0 & 0 & 0\\
            0 & 0 & 0 & 0 & 0 & 1 & 0 & 0\\
            0 & 0 & 0 & 0 & 0 & 0 & 0 & 1\\
            0 & 0 & 0 & 0 & 0 & 0 & 1 & 0
        \end{pmatrix}\end{split}`
    },
    ccz: {
        id: 7,
        type: 1,
        information: String.raw`CCZ gate. In the computational basis, this gate flips the phase of the target qubit if the control qubits are in the \(|11\rangle\)
        state. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split}CCZ\ q_0, q_1, q_2 & =
        I \otimes I \otimes |0\rangle\langle 0| + CZ \otimes |1\rangle\langle 1| \\ & =
        \begin{pmatrix}
            1 & 0 & 0 & 0 & 0 & 0 & 0 & 0 \\
            0 & 1 & 0 & 0 & 0 & 0 & 0 & 0 \\
            0 & 0 & 1 & 0 & 0 & 0 & 0 & 0 \\
            0 & 0 & 0 & 1 & 0 & 0 & 0 & 0 \\
            0 & 0 & 0 & 0 & 1 & 0 & 0 & 0 \\
            0 & 0 & 0 & 0 & 0 & 1 & 0 & 0 \\
            0 & 0 & 0 & 0 & 0 & 0 & 1 & 0 \\
            0 & 0 & 0 & 0 & 0 & 0 & 0 & -1
        \end{pmatrix}\end{split} <br/>
        `
    },
    swap: {
        id: 7,
        type: 1,
        information: String.raw`The SWAP gate. This is a symmetric and Clifford gate. <br/><br/>
        <strong>Matrix Representation:</strong>
        \begin{split} \text{SWAP} =
        \begin{pmatrix}
            1 & 0 & 0 & 0 \\
            0 & 0 & 1 & 0 \\
            0 & 1 & 0 & 0 \\
            0 & 0 & 0 & 1
        \end{pmatrix}\end{split}
        The gate is equivalent to a state swap and is a classical logic gate. <br/>
        \begin{split}|a, b\rangle \rightarrow |b, a\rangle\end{split}
        `
    }
}

export default info;
